import { InputMapService } from '../services/input-map.service';
import { IDeviceNode } from './device';
import { ElementTypes, ICardDataViewModel, IControlModel, IDisplayInfo } from './models';
import { NullDeviceModel } from './null-device-model';
import { DashboardDisplayInfoInterface } from '../../device-model/dashboard-display-info.interface';
import { CardData } from '../../device-model/model/card-data';
import { GraphicCardData } from '../../device-model/model/graphic-card-data';
import { MoreLessCardData } from '../../device-model/model/more-less-card-data';
import { SectionCardData } from '../../device-model/model/section-card-data';
import { NodeDeviceCategory } from '../../device-model/tree/node-device-category';
import { NodeDeviceSection } from '../../device-model/tree/node-device-section';
import { NodeElement } from '../../device-model/tree/node-element';
import { NodeElementList } from '../../device-model/tree/node-element-list';
import { NodeLeaf } from '../../device-model/tree/node-leaf';
import { NodeMoreLess } from '../../device-model/tree/node-more-less';
import { GraphicBehaviorType } from './graphic-behavior-type.enum';

export class DynamicDeviceModel extends NullDeviceModel implements DashboardDisplayInfoInterface {
    public static MAX_ITEMS_IN_LIST = 5;

    public setup(control: IControlModel) {
        const summaryViewModel = this.setupEventless(control);

        if (this.eventless) {
            return;
        }

        this.deviceModelService.setupLiveViewModels(this.locationId, this.areaId, summaryViewModel);

        control.stopLiveModelUpdates =
            () => this.deviceModelService.stopLiveViewModels(this.locationId, this.areaId, summaryViewModel);
    }

    public setupEventless(control: IControlModel) {
        const summaryViewModel = control.controlSummaryViewModel;

        if (summaryViewModel.isResponding) {
            this.updateStatus(control, 'Ready');
        }

        this.updateCard(control, summaryViewModel);

        return summaryViewModel;
    }

    public updateCard(control: IControlModel, viewModel: CASLink.Device.Ui.ViewModel.DynamicControls.ControlSummaryViewModel) {
        if (!viewModel.isResponding) {
            this.updateStatus(control, 'Not Responding');
        }

        control.displayInfo = this.getDisplayInfo(viewModel);
    }

    public getDisplayInfo(viewModel: CASLink.Device.Ui.ViewModel.DynamicControls.ControlSummaryViewModel): IDisplayInfo {
        let root: NodeElement = this.parseViewModelToTreeStructure(viewModel);

        let notUniqueNodeNames = DynamicDeviceModel.findNotUniqueNodeNames(root, []);
        DynamicDeviceModel.makeNamesUnique(root, notUniqueNodeNames);

        let displayInfo = this.convertTreeToDataViewModel(root);

        DynamicDeviceModel.extendDisplayInfo(displayInfo);

        return displayInfo;
    }

    private static setDeviceMinMaxSetpointValue(device: IDisplayInfo, minValue: number, maxValue: number, occupied: boolean) {
        const minProp = occupied ? 'occupiedSetpointMinimum' : 'unoccupiedSetpointMinimum';
        const maxProp = occupied ? 'occupiedSetpointMaximum' : 'unoccupiedSetpointMaximum';
        if (device[minProp] === undefined || minValue < device[minProp]) {
            device[minProp] = minValue;
        }
        if (device[maxProp] === undefined || maxValue > device[maxProp]) {
            device[maxProp] = maxValue;
        }
    }

    private static setDeviceOccupancy(
        device: IDisplayInfo,
        on: boolean,
        visible: boolean,
        enabled: boolean,
        sensorId: number,
        name: string,
    ) {
        device.occupancy = {};
            device.occupancy.occupiedOn = on;
            device.occupancy.occupiedVisible = visible;
            device.occupancy.occupiedEnabled = enabled;

        if (name !== null) {
            device.occupancy.occupiedName = name;
        }

        device.occupancy.occupiedSensorId = sensorId;
    }

    private static makeNamesUnique(nodeElement: NodeElement, notUniqueNodeNames: string[]) {
        const notUniqueNodeNamesSet = new Set(notUniqueNodeNames);
        nodeElement.children.forEach(child => {
            if (child instanceof NodeLeaf) {
                let leafFullName = child.getNameAsString();
                notUniqueNodeNamesSet.forEach(notUniqueNodeName => {
                    if (notUniqueNodeName === leafFullName) {
                        child.makeUnique();
                    }
                });
            } else {
                DynamicDeviceModel.makeNamesUnique(child, Array.from(notUniqueNodeNamesSet.values()));
            }
        });
    }

    private createGraphicCardData(data: IDeviceNode, indent: number, hidableKeys: string[], categories?: string[]): ICardDataViewModel {
        return new GraphicCardData(
            data,
            this.getDisplayValue(data),
            InputMapService.instance ? InputMapService.instance.getNumberInputMapping(data.elementType) : undefined,
            indent,
            hidableKeys,
            categories
        );
    }

    private createCardData(data: IDeviceNode, indent: number, hidableKeys: string[], categories: string[]) {
        return new CardData(
            data,
            this.getDisplayValue(data),
            InputMapService.instance ? InputMapService.instance.getNumberInputMapping(data.elementType) : undefined,
            indent,
            hidableKeys,
            categories
        );
    }

    private getDisplayValue(property: IDeviceNode) {
        if (property !== null && typeof property === 'object') {
            // tslint:disable-next-line: triple-equals
            if (property.elementType == ElementTypes.DateTimeSensor) {
                return this.formatDate(property.value);
            }

            if (typeof property.displayValue !== 'undefined') {
                return property.displayValue;
            }

            if (typeof property.value !== 'undefined') {
                return DynamicDeviceModel.getPropertyValueAsString(property.value);
            }

            return '';
        }
    }

    private static getPropertyValueAsString(value: any) {
        let displayValue: string;

        switch (value) {
            case true:
                displayValue = 'Yes';
                break;
            case false:
                displayValue = 'No';
                break;
            default:
                // tslint:disable-next-line: prefer-conditional-expression
                if (typeof value === 'number') {
                    displayValue = value.toFixed(1).toString();
                } else {
                    displayValue = value.toString();
                }
                break;
        }

        return displayValue;
    }

    private static isDeviceSection(property: IDeviceNode) {
        return property.elementType && property.elementType === ElementTypes.DeviceSection;
    }

    private static isElementList(property: IDeviceNode) {
        return property.elementType && property.elementType === ElementTypes.ElementList;
    }

    private static isDeviceCategory(property: IDeviceNode) {
        return property.elementType && property.elementType === ElementTypes.DeviceCategory;
    }

    private static isLeaf(property: IDeviceNode) {
        for (const key in property) {
            if (key !== 'changeCommand' && key !== 'fieldRelationship' && property.hasOwnProperty(key) && key !== 'valueSet') {
                const current = property[key];

                if ((current !== null && typeof current === 'object') || Array.isArray(current)) {
                    return false;
                }
            }
        }

        return true;
    }

    private processElementList(property: any, name: string, parent: NodeElement) {
        if (!property.isVisible) {
            return null;
        }
        const section = new NodeDeviceSection(parent, property);
        const nodeElementList = new NodeElementList(parent, name);

        if (property.items) {
            property.items.forEach((item) => {
                let value: IDeviceNode = item.value;
                if (value.isVisible) {
                    let nodeElementListItemSection = new NodeDeviceSection(nodeElementList, item.value);
                    this.recurseProperty(value, nodeElementListItemSection);
                    if (nodeElementListItemSection.children.length > 0) {
                        nodeElementList.push(nodeElementListItemSection);
                    }
                }
            });
        }

        if (nodeElementList.children.length === 0) {
            return null;
        }

        if (nodeElementList.children.length > DynamicDeviceModel.MAX_ITEMS_IN_LIST) {
            const n = nodeElementList.children[0];
            const hidableKey = nodeElementList.getFullNameAsString();

            for (let i = DynamicDeviceModel.MAX_ITEMS_IN_LIST; i < nodeElementList.children.length; i++) {
                nodeElementList.children[i].addHidableKey(hidableKey);
            }
            nodeElementList.push(new NodeMoreLess(hidableKey, n.indent));
        }

        section.push(nodeElementList);

        return section;
    }

    private createSection(property: any, parent: NodeElement) {
        if (!property.isVisible) {
            return null;
        }
        let node = new NodeDeviceSection(parent, property);

        this.recurseProperty(property, node);

        // Skip section with only one child and take only its leaf instead
        if (node.children && node.children.length === 1 && (node.children[0] instanceof NodeLeaf)) {
            node.children[0].parent = parent;
            if (node.children[0].data) {
                node.children[0].data.order = node.data.order;
                node.children[0].skippedNode = node;
            }
            return node.children[0];
        }
        return node;
    }

    private static createLeaf(property: any, parent: NodeElement) {
        if (!property.isVisible ||
            (property.graphicBehavior === GraphicBehaviorType.ALERT && !property.value)) {
            return null;
        }
        return new NodeLeaf(parent, property);
    }

    private createDeviceCategory(property: any, parent: NodeElement) {
        if (!property.isVisible) {
            return null;
        }
        let node = new NodeDeviceCategory(parent, property);

        this.recurseProperty(property, node);

        return node;
    }

    private recurseProperty(property: any, parent: NodeElement) {
        // eslint-disable-next-line guard-for-in
        for (const key in property) {
            const currentSubProperty: IDeviceNode = property[key];

            if (currentSubProperty !== null && typeof currentSubProperty === 'object' && currentSubProperty.isVisible) {
                if (DynamicDeviceModel.isElementList(currentSubProperty)) {
                    parent.push(this.processElementList(currentSubProperty, key, parent));
                } else if (DynamicDeviceModel.isDeviceSection(currentSubProperty)) {
                    parent.push(this.createSection(currentSubProperty, parent));
                } else if (DynamicDeviceModel.isDeviceCategory(currentSubProperty)) {
                    parent.push(this.createDeviceCategory(currentSubProperty, parent));
                } else if (DynamicDeviceModel.isLeaf(currentSubProperty)) {
                    parent.push(DynamicDeviceModel.createLeaf(currentSubProperty, parent));
                } else {
                    this.recurseProperty(currentSubProperty, parent);
                }
            }
        }
    }

    private parseViewModelToTreeStructure = (viewModel: any) => {
        let root: NodeElement = new NodeElement(null, ElementTypes.Root);
        this.recurseProperty(viewModel, root);

        return root;
    }

    private static findNotUniqueNodeNames(nodeElement: NodeElement, firstVisit: string[]) {
        let notUniqueList: string[] = [];

        nodeElement.children.forEach(c => {
            if (c instanceof NodeLeaf) {
                let leafFullName = c.getNameAsString();
                if (firstVisit.find(fv => fv === leafFullName)) {
                    notUniqueList.push(leafFullName);
                } else {
                    firstVisit.push(leafFullName);
                }
            } else {
                notUniqueList.push(...DynamicDeviceModel.findNotUniqueNodeNames(c, firstVisit));
            }
        });

        return notUniqueList;
    }

    private convertTreeToDataViewModel(root: NodeElement) {
        let result: ICardDataViewModel[] = [];
        let isCardEditable = false;

        root['$discovered'] = true;
        let queue: NodeElement[] = [];

        queue.push(root);
        while (queue.length) {
            let v = queue.pop();
            const data = v.data;

            if (v instanceof NodeLeaf) {
                if (data.graphicBehavior) {
                    result.push(this.createGraphicCardData(data, v.indent, v.hidableKeys, v.categories));
                } else {
                    result.push(this.createCardData(data, v.indent, v.hidableKeys, v.categories));
                }
                const isCardEditableCondition = data.isEnabled && data.isVisible;
                if (isCardEditableCondition) {
                    isCardEditable = true;
                }
            } else if (v instanceof NodeDeviceSection && v.data && v.data.isVisible) {
                let section = new SectionCardData(data, (v && v.data && v.data.displayText) ? v.data.displayText : 'category',
                    v.indent,
                    v.hidableKeys);
                section.isVisible = v.$markedAsVisibleSection;
                result.push(section);
            } else if (v instanceof NodeMoreLess) {
                result.push(new MoreLessCardData(v));
            }

            v.children.slice().reverse().forEach(c => {
                if (!c['$discovered']) {
                    c['$discovered'] = true;
                    queue.push(c);
                }
            });
        }
        const dataViewModel = result.filter(m => {
            return m.isVisible;
        });

        return {
            dataViewModel: dataViewModel,
            isCardEditable: isCardEditable
        } as IDisplayInfo;
    }

    private static extendDisplayInfo(displayInfo: IDisplayInfo) {
        let hasTemperatureGraphic = false;

        displayInfo.dataViewModel?.forEach(d => {
            if (!hasTemperatureGraphic && d.graphicBehavior === GraphicBehaviorType.TEMPERATURE_SENSOR) {
                hasTemperatureGraphic = true;

                const temperature = Math.round(parseFloat(d.actualValue));
                displayInfo.temperature = {text: temperature.toString(), units: '°'};
            } else if (d.graphicBehavior === GraphicBehaviorType.OCCUPIED_SENSOR) {
                DynamicDeviceModel.setDeviceOccupancy(displayInfo, !!d.actualValue, d.isVisible, d.isEnabled, d.sensorId, d.name);
            } else if (
                d.graphicBehavior === GraphicBehaviorType.TEMPERATURE_COLD_SENSOR &&
                (
                    displayInfo.occupiedCoolingSetpoint === undefined ||
                    displayInfo.occupiedCoolingSetpointSensorId === d.sensorId
                )
            ) {
                displayInfo.occupiedCoolingSetpoint = parseFloat(d.actualValue);
                displayInfo.occupiedCoolingSetpointSensorId = d.sensorId;
                DynamicDeviceModel.setDeviceMinMaxSetpointValue(displayInfo, d.data.minValue, d.data.maxValue, true);
            } else if (
                d.graphicBehavior === GraphicBehaviorType.TEMPERATURE_COLD_UNOCCUPIED_SENSOR &&
                (
                    displayInfo.unoccupiedCoolingSetpoint === undefined ||
                    displayInfo.unoccupiedCoolingSetpointSensorId === d.sensorId
                )
            ) {
                displayInfo.unoccupiedCoolingSetpoint = parseFloat(d.actualValue);
                displayInfo.unoccupiedCoolingSetpointSensorId = d.sensorId;
                DynamicDeviceModel.setDeviceMinMaxSetpointValue(displayInfo, d.data.minValue, d.data.maxValue, false);
            } else if (
                d.graphicBehavior === GraphicBehaviorType.TEMPERATURE_HOT_SENSOR &&
                (
                    displayInfo.occupiedHeatingSetpoint === undefined ||
                    displayInfo.occupiedHeatingSetpointSensorId === d.sensorId
                )
            ) {
                displayInfo.occupiedHeatingSetpoint = parseFloat(d.actualValue);
                displayInfo.occupiedHeatingSetpointSensorId = d.sensorId;
                DynamicDeviceModel.setDeviceMinMaxSetpointValue(displayInfo, d.data.minValue, d.data.maxValue, true);
            } else if (
                d.graphicBehavior === GraphicBehaviorType.TEMPERATURE_HOT_UNOCCUPIED_SENSOR &&
                (
                    displayInfo.unoccupiedHeatingSetpoint === undefined ||
                    displayInfo.unoccupiedHeatingSetpointSensorId === d.sensorId
                )
            ) {
                displayInfo.unoccupiedHeatingSetpoint = parseFloat(d.actualValue);
                displayInfo.unoccupiedHeatingSetpointSensorId = d.sensorId;
                DynamicDeviceModel.setDeviceMinMaxSetpointValue(displayInfo, d.data.minValue, d.data.maxValue, false);
            } else if (
                d.graphicBehavior === GraphicBehaviorType.RELATIVE_HUMIDITY_SENSOR
            ) {
                displayInfo.relativeHumidity = {text: Math.round(parseFloat(d.actualValue)).toString(), units: ''};
            }
        });

        displayInfo.editableProperties = {};
        displayInfo.editableProperties.occupancy = {...displayInfo.occupancy};
        displayInfo.editableProperties.occupiedCoolingSetpoint = displayInfo.occupiedCoolingSetpoint;
        displayInfo.editableProperties.occupiedHeatingSetpoint = displayInfo.occupiedHeatingSetpoint;
        displayInfo.editableProperties.unoccupiedCoolingSetpoint = displayInfo.unoccupiedCoolingSetpoint;
        displayInfo.editableProperties.unoccupiedHeatingSetpoint = displayInfo.unoccupiedHeatingSetpoint;
    }
}
